/**
 * Mirroring the layout on Android.
 *
 * The web build and iOS mirror a subtree from a `direction` style. Android on
 * this architecture ignores that style: its layout direction is fixed from
 * I18nManager when the React context is created. So on Android the switch is
 * made natively and the context rebuilt — the donor sees the app reopen in
 * Urdu, laid out right-to-left, rather than Urdu words in an English layout.
 *
 * Only ever called once the stored language is known. Before that the app is
 * showing its default, and "fixing" the direction to match a default would
 * restart a donor who had chosen Urdu back into English, and back again.
 */

import { I18nManager, NativeModules, Platform } from 'react-native';

/** True when a restart was started, so the caller can stop rendering. */
export function applyNativeDirection(isRTL: boolean): boolean {
  if (Platform.OS !== 'android') return false;
  // Already right: the common case on every launch after the first switch.
  if (I18nManager.isRTL === isRTL) return false;

  I18nManager.allowRTL(true);
  I18nManager.forceRTL(isRTL);

  const restart = (NativeModules.AppRestart as { restart?: () => void } | undefined)?.restart;
  if (!restart) return false;
  restart();
  return true;
}

/**
 * Whether the platform itself swaps every left and right — positions, and a
 * text's alignment — because the app is laid out right-to-left natively.
 *
 * Only Android does, and only once `applyNativeDirection` has made the app
 * right-to-left. A style that means a physical side (a drawing's parts; a
 * line of text held to the side its language starts on) has to give the
 * opposite side there, so the swap lands it where it was meant to be.
 */
export function platformSwapsSides(): boolean {
  return Platform.OS === 'android' && I18nManager.isRTL;
}
